import PropTypes from 'prop-types';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import Layout from '../../components/Layout/Layout';
import SEO from '../../components/SEO/SEO';
import CTASimple from '../../components/CTASimple/CTASimple';
import theme from '../../styles/theme';
import HeroOverlap from '../../components/HeroOverlap/HeroOverlap';
import Featurette from '../../components/Featurette/Featurette';
import Picture from '../../components/Shared/Picture';
import ValueSection from '../../components/ValueSection/ValueSection';
import { getPageContent } from '../../services/dpc';
import Error from '../../components/Error/Error';
import useGTM from '../../hooks/useGTM';
import EmbeddedVideoSection from '../../components/EmbeddedVideoSection/EmbeddedVideoSection';
import linkRenderer from '../../util/renderers/link';

function About({
  data: {
    contentCTAs,
    contentVideos,
    faqPod,
    heroCta,
    heroMedia,
    heroText,
    mobileHeroMedia,
    metaDescription,
    preFooterCta,
    tipsAndToolsPod,
    titleTag,
    customerReviewPod,
    ...props
  } = {},
  error,
  isLoading,
}) {
  if (error) {
    console.error(error);
    return (
      <Layout>
        <Error />
      </Layout>
    );
  }
  const [triggerGTMEvent] = useGTM();

  return (
    <Layout isLoading={isLoading}>
      <SEO title={titleTag} description={metaDescription} />

      <HeroOverlap
        position='left'
        background={heroMedia?.[0]?.media?.fields?.file?.url}
        mobileBackground={mobileHeroMedia?.[0]?.media?.fields?.file?.url}
        alt={heroMedia?.[0]?.altText}
      >
        <h1>Welcome to Vanderbilt Mortgage</h1>
        <ReactMarkdown source={heroText} />
      </HeroOverlap>

      <Featurette
        primary={
          <Picture
            src='https://images.ctfassets.net/y64yks2od8ab/zGMexOgrhtRNpPHPhKfKU/2d245df530e429427fc20e2fa29027d7/preowned-home.jpg?w=520'
            alt='About Vanderbilt Mortgage'
          />
        }
        secondary={
          <div>
            <h2>About Vanderbilt Mortgage</h2>
            <p>
              Vanderbilt Mortgage and Finance, Inc. offers a variety of home
              loan product services including:
            </p>
            <ul>
              <li>New and used manufactured and modular home financing</li>
              <li>Servicing manufactured and modular home loans</li>
            </ul>
          </div>
        }
        center={true}
      />

      <Featurette
        primary={
          <div>
            <h2>Our Mission</h2>
            <p>
              It is our mission to help families achieve the Great American
              Dream of homeownership. We have been helping families across the
              nation for over 40 years and currently service over 200,000
              customer loans. Our team members are committed to supporting our
              customers through the home loan process and building trusting
              relationships that last throughout the life of the loan and
              beyond.
            </p>
          </div>
        }
        secondary={
          <Picture
            src='https://images.ctfassets.net/y64yks2od8ab/nfaLRJL7Uip15tGI6JJpA/fc84ea83066ccef49632dbecbf83f132/vmf-our-mission.jpg?w=520'
            alt='Our Mission'
          />
        }
        reverseOnMobile={true}
        center={true}
      />

      {contentVideos?.[0]?.videoSource &&
        contentVideos?.[0]?.embeddedContent && (
          <EmbeddedVideoSection
            backgroundColor={contentVideos?.[0]?.embeddedContentBgColor}
            title={contentVideos?.[0]?.embeddedContentHeader}
            intro={
              <ReactMarkdown
                source={contentVideos?.[0]?.embeddedContentIntro}
                renderers={{
                  link: linkRenderer,
                }}
              />
            }
            disclaimer={
              <ReactMarkdown
                source={contentVideos?.[0]?.disclaimer}
                renderers={{
                  link: linkRenderer,
                }}
              />
            }
            video={{
              source: contentVideos?.[0]?.videoSource,
              content: contentVideos?.[0]?.embeddedContent,
            }}
            thumbnail={{
              alt: contentVideos?.[0]?.thumbnail?.fields?.description,
              src: contentVideos?.[0]?.thumbnail?.fields?.file?.url,
            }}
            margin={{
              top: '5rem',
            }}
          />
        )}

      <ValueSection />

      <CTASimple
        title={preFooterCta?.ctaHeader || 'Find the Perfect Home Loan for You'}
        link={{
          to: preFooterCta?.firstCTAButtonUrl || '/applynow/welcome/',
          title: preFooterCta?.firstCTAButtonText || 'Apply Today',
          isExternal: true,
          newTab: true,
          size: 'large',
          onClick: triggerGTMEvent({
            name: 'vmf.cta_click',
            location: 'About VMF',
            category: 'job_application',
            action: 'button',
            label: 'job_openings',
          }),
        }}
        background={theme.colors.claytonBlue}
      >
        <ReactMarkdown source={preFooterCta?.ctaIntro} />
      </CTASimple>
    </Layout>
  );
}

About.getInitialProps = async ({}) => {
  try {
    const data = await getPageContent('/about/');

    if (data.status !== 200) {
      throw data.statusText;
    }

    return {
      data: data.data.result,
    };
  } catch (error) {
    return {
      error,
    };
  }
};

About.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  data: PropTypes.shape({}),
  error: PropTypes.object,
};

export default About;
